import { getSearchResultsLegacy } from '@/clients/gg/search'
import { BookingOption } from '@/organisms/FlightSearchNew/types'
import { TripId } from '@/types/common'
import { GGClientError } from '@/types/error'
import {
	FlightCreateBookingSegmentRequest,
	FlightCreateBookingSegmentRequestZ,
	FlightCreateBookingSegmentResultZ,
	FlightObFeeRecalculateGetResponse,
	FlightObFeeRecalculateGetResponseZ,
	FlightObFeeRecalculateRequest,
	FlightObFeeRecalculateRequestZ,
} from '@/types/flights/booking'
import { AirlineZ, AllianceZ, FlightCarrierCode } from '@/types/flights/common'
import {
	FlightFaresPricingCreateJobRequestZ,
	FlightFaresPricingCreateJobResponseZ,
	FlightFaresPricingJobResultResponseZ,
} from '@/types/flights/fare'
import { GetFlightSearchResultsResponse, GetFlightSearchResultsResponseZ } from '@/types/flights/search'
import { JobGroupResult } from '@/types/job'
import { SearchId } from '@/types/search'

import { getGGClient, JobRequestOverrides, mapJobResult, RequestOverrides } from './GetGoingClient'

export async function createFlightBookingSegment(
	tripId: TripId,
	bookingData: FlightCreateBookingSegmentRequest,
	overrides?: RequestOverrides,
) {
	return getGGClient()
		.simplePollingRequest(`trips/${tripId}/v2/flights`, {
			requestSchema: FlightCreateBookingSegmentRequestZ,
			resultSchema: FlightCreateBookingSegmentResultZ,
			data: bookingData,
			...overrides,
		})
		.catch(
			GGClientError.matchNotMappedError({
				fare_unavailable: true,
				result_expired: true,
			}),
		)
}

export async function getFlightFaresPricing(
	searchId: SearchId,
	data: { bookingOption: BookingOption; fareFamilies?: boolean }[],
	overrides?: JobRequestOverrides,
) {
	const url = `flights/pricing` as const
	const { job_id } = await getGGClient()
		.request('POST', url, {
			requestSchema: FlightFaresPricingCreateJobRequestZ,
			responseSchema: FlightFaresPricingCreateJobResponseZ,
			data: data.map(({ bookingOption, fareFamilies }) => ({
				fare_group_key: bookingOption.fare_group_key,
				flight_option_keys: bookingOption.flights.map((fo) => fo.flight_option_key),
				flight_identifier: bookingOption.flight_identifier,
				user_id: bookingOption.flight_option_keys![0].traveler_id,
				search_id: searchId,
				fare_families: fareFamilies,
			})),
			signal: overrides?.signal,
			...overrides?.create,
		})
		.then((r) => r.data)

	const result = await getGGClient().pollJobResult(url, {
		params: {
			job_id,
			search_id: searchId,
		},
		responseSchema: FlightFaresPricingJobResultResponseZ,
		mapJobResult,
		signal: overrides?.signal,
		...overrides?.poll,
	})

	// Temporary while Zod types haven replaced old types yet
	return result as unknown as JobGroupResult<{ fare_groups: BookingOption[] }>
}

/** Legacy workflow: doesn't throw of failure */
export async function getFlightSearchResultsLegacy(searchId: SearchId, overrides?: RequestOverrides) {
	return getSearchResultsLegacy(searchId, {
		// @ts-expect-error -- A temporary hack to override the response schema
		responseSchema: GetFlightSearchResultsResponseZ,
		...overrides,
	}).then((r) => r as GetFlightSearchResultsResponse)
}

export async function getAlliances(overrides?: RequestOverrides) {
	return getGGClient()
		.request('GET', 'alliances', {
			responseSchema: AllianceZ.array(),
			...overrides,
		})
		.then((r) => r.data)
}

getAlliances.queryKey = ['alliances']

export async function searchAirlines(query: string, overrides?: RequestOverrides) {
	return getGGClient()
		.request('GET', 'airlines', {
			params: { search: query },
			responseSchema: AirlineZ.array(),
			...overrides,
		})
		.then((r) => r.data)
}

export async function getAirline(iata: FlightCarrierCode, overrides?: RequestOverrides) {
	const airlines = await searchAirlines(iata, overrides)
	return airlines.find((airline) => airline.iata === iata)
}

getAirline.queryKey = (iata: FlightCarrierCode) => ['airlines', iata]
export async function recalculateFlightObFee(
	tripId: number,
	data: FlightObFeeRecalculateRequest,
	overrides?: RequestOverrides,
): Promise<FlightObFeeRecalculateGetResponse> {
	return getGGClient().simplePollingRequest(`trips/${tripId}/flights/ob_fee`, {
		requestSchema: FlightObFeeRecalculateRequestZ,
		resultSchema: FlightObFeeRecalculateGetResponseZ,
		data,
		...overrides,
	})
}
